import React, { useEffect, useRef } from 'react';
import * as Highcharts from 'highcharts';

interface StackedBarData {
    name: string;
    acceptedCount: number;
    rejectedCount: number;
}

interface LivisStackedBarHighchartsProps {
    data: StackedBarData[];
    categories?: string[];
    height?: number;
    tooltip?: boolean;
    showLegend?: boolean;
    stacking?: 'normal' | 'percent';
    pointWidth?: number;
    borderRadius?: number;
    onPointClick?: (point: any) => void;
    colors?: {
        accepted: string;
        rejected: string;
    };
    y_title?: string;
}

const LivisStackedBarHighcharts: React.FC<LivisStackedBarHighchartsProps> = ({
    data = [],
    categories,
    height = 400,
    tooltip = true,
    showLegend = false,
    stacking = 'normal',
    pointWidth = 50,
    borderRadius = 8,
    onPointClick,
    y_title,
    colors = {
        accepted: 'var(--livis-success-color)',
        rejected: 'var(--livis-part-rejected-color)'
    }
}) => {
    const chartRef = useRef<HTMLDivElement>(null);
    const chartInstance = useRef<Highcharts.Chart | null>(null);

    useEffect(() => {
        if (!chartRef.current || !data.length) return;

        // Transform data for Highcharts stacked bar
        const acceptedData = data.map(item => ({
            name: item.name,
            y: item.acceptedCount,
            category: item.name
        }));

        const rejectedData = data.map(item => ({
            name: item.name,
            y: item.rejectedCount,
            category: item.name
        }));

        const chartOptions: Highcharts.Options = {
            chart: {
                type: 'column',
                height: height,
                backgroundColor: 'transparent',
                spacing: [10, 10, 10, 10] // Increased bottom spacing for labels
            },
            title: {
                text: undefined
            },
            subtitle: {
                text: undefined
            },
            xAxis: {
                categories: categories || data.map(item => item.name),
                labels: {
                    formatter: function() {
                        const item = data[this.pos];
                        // return HTML that uses class names only; styles are in common_styles.scss
                        // we include data attributes with resolved colors so CSS-independent consumers can read them if needed
                        return `
                            <div class="livis-stacked-bar-highchart-xaxis-label" style="display: flex; flex-direction: column; align-items: center;" data-accepted-color="${colors.accepted}" data-rejected-color="${colors.rejected}">
                                <div class="livis-stacked-bar-highchart-xaxis-title">${item?.name}</div>
                                <div class="livis-stacked-bar-highchart-xaxis-meta livis-stacked-bar-highchart-accepted">${item?.acceptedCount || 20} Accepted</div>
                                <div class="livis-stacked-bar-highchart-xaxis-meta livis-stacked-bar-highchart-rejected">${item?.rejectedCount || 20} Rejected</div>
                            </div>
                        `;
                    },
                    useHTML: true,
                    y: 20 // Move labels down to create space
                },
                lineWidth: 0,
                tickWidth: 0,
                gridLineWidth: 0
            },
            yAxis: {
                min: 0,
                title: {
                    text: y_title,
                    style: {
                        fontSize: '14px',
                        fontWeight: '600'
                    }
                },
                labels: {
                    formatter: function() {
                        return `${(this.value as number / 1000).toFixed(0)}k`;
                    }
                },
                
                gridLineColor: 'var(--livis-dashboard-card-box-shadow)',
                gridLineWidth: 1
            },
            legend: {
                enabled: showLegend,
                align: 'right',
                verticalAlign: 'top',
                itemStyle: {
                    color: '#64748B',
                    fontSize: '12px',
                    fontWeight: '500'
                }
            },
            tooltip: {
                enabled: tooltip,
                shared: true,
                backgroundColor: 'var(--livis-recipe-details-part-footer-color)',
                borderColor: 'transparent',
                borderRadius: 8,
                style: {
                    color: 'var(--livis-white-color)'
                },
                    formatter: function() {
                    const point = this.point;
                    const categoryIndex = point?.x;
                    const item = data[categoryIndex || 0];

                    return `
                        <div class="livis-stacked-bar-highchart-tooltip" data-accepted-color="${colors.accepted}" data-rejected-color="${colors.rejected}">
                            <div class="livis-stacked-bar-highchart-tooltip-title">${item?.name}</div>
                            <div class="livis-stacked-bar-highchart-tooltip-row">
                                <span class="livis-stacked-bar-highchart-tooltip-dot livis-stacked-bar-highchart-accepted-dot"></span>
                                <span class="livis-stacked-bar-highchart-tooltip-label">Accepted:</span>
                                <span class="livis-stacked-bar-highchart-tooltip-value">${item?.acceptedCount?.toLocaleString()}</span>
                            </div>
                            <div class="livis-stacked-bar-highchart-tooltip-row">
                                <span class="livis-stacked-bar-highchart-tooltip-dot livis-stacked-bar-highchart-rejected-dot"></span>
                                <span class="livis-stacked-bar-highchart-tooltip-label">Rejected:</span>
                                <span class="livis-stacked-bar-highchart-tooltip-value">${item?.rejectedCount?.toLocaleString()}</span>
                            </div>
                        </div>
                    `;
                },
                useHTML: true
            },
            plotOptions: {
                column: {
                    stacking: stacking,
                    pointWidth: pointWidth,
                    borderWidth: 0,
                    dataLabels: {
                        enabled: false
                    },
                    // We'll set border radius per series below
                    point: {
                        events: {
                            click: function() {
                                if (onPointClick) {
                                    const partName = this.name || this.category || this.options?.name || 
                                                    categories?.[this.x] || data[this.x]?.name || `Part ${this.x + 1}`;
                                    
                                    const categoryIndex = this.x; // x index of the clicked bar
                                    const clickedData = data[categoryIndex];
                                    
                                    onPointClick({
                                        name: partName,
                                        category: partName,
                                        acceptedCount: clickedData?.acceptedCount || 0,
                                        rejectedCount: clickedData?.rejectedCount || 0,
                                        total: (clickedData?.acceptedCount || 0) + (clickedData?.rejectedCount || 0),
                                        index: categoryIndex
                                    });
                                }
                            }
                        }
                    }
                }
            },
            series: [
                {
                    name: 'Accepted',
                    type: 'column',
                    data: acceptedData,
                    color: colors.accepted,
                    stack: 'total',
                    borderRadius: 0
                },
                {
                    name: 'Rejected',
                    type: 'column',
                    data: rejectedData,
                    color: colors.rejected,
                    stack: 'total',
                    borderRadius: 0

                }
            ],
            credits: {
                enabled: false
            },
            exporting: {
                enabled: false
            }
        };

        // Destroy existing chart
        if (chartInstance.current) {
            chartInstance.current.destroy();
        }

        // Create new chart
        chartInstance.current = Highcharts.chart(chartRef.current, chartOptions);

        // Cleanup function
        return () => {
            if (chartInstance.current) {
                chartInstance.current.destroy();
                chartInstance.current = null;
            }
        };
    }, [data, categories, height, tooltip, showLegend, stacking, pointWidth, borderRadius, colors, onPointClick]);

    return <div ref={chartRef} className="livis-stacked-bar-highchart-container" />;
};

export default LivisStackedBarHighcharts;